import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Car, Cpu, Sun, Droplets, Bike, MapPin, MonitorSmartphone, Plane, Radio, Battery, Wifi, TrafficCone, Leaf } from "lucide-react";

// Electric bus with charging station - sustainable public transport
const VISION_IMAGE = "https://customer-assets.emergentagent.com/job_green-tech-hyd/artifacts/r6ap8va7_WhatsApp%20Image%202026-04-06%20at%2011.46.47%20%282%29.jpeg";

const visionPoints = [
  { icon: Car, label: "E-Mobility" },
  { icon: Droplets, label: "Hydrogen Tech" },
  { icon: Sun, label: "Solar Energy" },
  { icon: Cpu, label: "AI & Autonomous" },
  { icon: MonitorSmartphone, label: "Software Defined Vehicles" },
  { icon: Bike, label: "Public Utility" },
  { icon: MapPin, label: "Last Mile" },
  { icon: Plane, label: "Urban Air Mobility & eVTOL" },
  { icon: Radio, label: "Drone Technology" },
  { icon: Battery, label: "Circular Economy in Battery" },
  { icon: Wifi, label: "Connected Vehicles" },
  { icon: TrafficCone, label: "Smart Infrastructure & V2X" },
  { icon: Cpu, label: "Smart City Traffic Optimisation" },
  { icon: Leaf, label: "Bio Fuels" },
];

const VisionSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="vision"
      ref={ref}
      data-testid="vision-section"
      className="py-20 md:py-32 bg-stone-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700 mb-4 block">
              Our Vision
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-stone-900 mb-6 tracking-tight">
              Making India a Global Hub for Sustainable Mobility
            </h2>
            <div className="space-y-4 text-stone-600 leading-relaxed mb-8">
              <p>
                Tech4Earth aims to contribute towards sustainable growth of
                Mobility technologies by fostering collaborations and partnerships
                across different markets and help all the key stakeholders —
                Consumers, Technology Providers, Manufacturers, and Government — to
                come together regularly.
              </p>
              <p>
                Our vision is to make India a global hub for R&D, manufacturing,
                and adoption of sustainable advanced mobility solutions including
                e-mobility, hydrogen and solar technologies, AI, autonomous
                vehicles, and innovative public utility vehicles.
              </p>
              <p className="font-medium text-stone-700">
                India is well-positioned to disrupt and innovate some of the most
                relevant sustainable technologies that can be applied for mass
                utility using efficient lean cost structures in the mobility sector.
              </p>
            </div>

            {/* Vision Tags */}
            <div className="flex flex-wrap gap-3">
              {visionPoints.map((point, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay: 0.3 + index * 0.1, duration: 0.4 }}
                  className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-stone-200 shadow-sm"
                >
                  <point.icon className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm font-medium text-stone-700">
                    {point.label}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={VISION_IMAGE}
                alt="Solar panels renewable energy"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/10 to-blue-600/10" />
            </div>
            {/* Decorative Elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-emerald-200/50 rounded-full blur-2xl" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-blue-200/50 rounded-full blur-2xl" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default VisionSection;
